/* eslint-disable max-len */
import { ReactElement, ReactNode, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { useClickOutside } from 'app/hooks';
import type { Folder } from 'app/hooks/useFolders';

interface Props {
  folders: Folder[];
  /** id of the folder this tour is currently in, or null if unfiled */
  currentFolderId: string | null;
  onMove: (folderId: string | null) => void;
  onCreate: (name: string) => string;
  /** When provided, renders a "Share" action at the end of the menu. */
  onShare?: () => void;
  /** Custom trigger content; defaults to a kebab icon. */
  trigger?: ReactNode;
  triggerAriaLabel?: string;
  triggerClassName?: string;
  /**
   * Extra primary action rendered as a black button at the very bottom of the
   * menu (below Share, if any) — e.g. "Rename".
   */
  primaryAction?: { label: string; icon?: ReactNode; onClick: () => void };
}

interface FlatEntry {
  folder: Folder;
  depth: number;
}

/** Depth-first flatten of the folder tree — children immediately follow parent. */
function flattenTree(folders: Folder[]): FlatEntry[] {
  const byParent = new Map<string | null, Folder[]>();
  for (const f of folders) {
    const key: string | null = f.parentId ?? null;
    const arr: Folder[] = byParent.get(key) ?? [];
    arr.push(f);
    byParent.set(key, arr);
  }
  byParent.forEach((arr: Folder[]) => {
    arr.sort((a: Folder, b: Folder) => b.createdAt - a.createdAt);
  });
  const out: FlatEntry[] = [];
  const visit = (parentId: string | null, depth: number) => {
    const children = byParent.get(parentId) ?? [];
    for (const child of children) {
      out.push({ folder: child, depth });
      visit(child.id, depth + 1);
    }
  };
  visit(null, 0);
  // Append any orphaned folders (parentId pointing at a deleted folder) as root
  const seen = new Set(out.map((e) => e.folder.id));
  for (const f of folders) {
    if (!seen.has(f.id)) out.push({ folder: f, depth: 0 });
  }
  return out;
}

const KebabIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="currentColor"
    stroke="none"
  >
    <circle cx="4" cy="10" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="16" cy="10" r="1.6" />
  </svg>
);

const RadioIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
    <circle
      cx="10"
      cy="10"
      r="7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle cx="10" cy="10" r="3.5" fill="currentColor" />
  </svg>
);

const ShareIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="4" r="2" />
    <circle cx="4" cy="8" r="2" />
    <circle cx="12" cy="12" r="2" />
    <path d="M5.7 7L10.3 5M5.7 9L10.3 11" />
  </svg>
);

export function MoveToFolderMenu({
  folders,
  currentFolderId,
  onMove,
  onCreate,
  onShare,
  trigger,
  triggerAriaLabel,
  triggerClassName,
  primaryAction,
}: Props): ReactElement {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const [newName, setNewName] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const flat = useMemo(() => flattenTree(folders), [folders]);

  useClickOutside(ref, (isOutside) => {
    if (isOutside) {
      setOpen(false);
      setMode('list');
      setNewName('');
    }
  });

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) {
      setMode('list');
      return;
    }
    const id = onCreate(name);
    onMove(id);
    setOpen(false);
    setMode('list');
    setNewName('');
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-label={triggerAriaLabel ?? 'Tour actions'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={
          triggerClassName ??
          classNames(
            'w-8 h-8 flex items-center justify-center bg-transparent border-0 shadow-none outline-none',
            'text-black/55 hover:text-black/85 hover:bg-transparent hover:shadow-none',
            'focus:outline-none focus:shadow-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
            'transition-opacity md:opacity-0 md:group-hover:opacity-100',
            open && 'md:opacity-100 text-black/85',
          )
        }
      >
        {trigger ?? <KebabIcon />}
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="glass-popover absolute top-full right-0 mt-2 min-w-[220px] p-1 z-20"
        >
          {mode === 'list' && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-black/40">
                Move to folder
              </div>

              <button
                type="button"
                onClick={() => {
                  onMove(null);
                  setOpen(false);
                }}
                className="flex items-center w-full text-left px-3 py-2 rounded-md text-[13px] font-medium text-black/75 hover:bg-black/[0.04] hover:text-black/95 transition-colors bg-transparent border-0 focus:outline-none"
              >
                <span className="flex-1 truncate">All Tours</span>
                {currentFolderId === null && (
                  <span className="ml-2 text-black/55">
                    <RadioIcon />
                  </span>
                )}
              </button>

              {folders.length > 0 && (
                <div className="my-1 h-px bg-black/[0.08] mx-2" />
              )}

              <div className="max-h-[240px] overflow-y-auto">
                {flat.map(({ folder, depth }) => (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => {
                      onMove(folder.id);
                      setOpen(false);
                    }}
                    style={{ paddingLeft: 12 + depth * 14 }}
                    className="flex items-center w-full text-left pr-3 py-2 rounded-md text-[13px] font-medium text-black/75 hover:bg-black/[0.04] hover:text-black/95 transition-colors bg-transparent border-0 focus:outline-none"
                  >
                    <span className="flex-1 truncate">{folder.name}</span>
                    {currentFolderId === folder.id && (
                      <span className="ml-2 text-black/55">
                        <RadioIcon />
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="my-1 h-px bg-black/[0.08] mx-2" />
              <button
                type="button"
                onClick={() => {
                  setMode('create');
                  setNewName('');
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-[13px] font-medium text-black/75 hover:bg-black/[0.04] hover:text-black/95 transition-colors bg-transparent border-0 focus:outline-none"
              >
                + New folder…
              </button>

              {onShare && (
                <>
                  <div className="my-1 h-px bg-black/[0.08] mx-2" />
                  <div className="px-1 pt-1 pb-1">
                    <button
                      type="button"
                      onClick={() => {
                        onShare();
                        setOpen(false);
                      }}
                      className="flex items-center justify-center gap-2 w-full h-9 rounded-md text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 focus:outline-none"
                    >
                      <ShareIcon />
                      Share
                    </button>
                  </div>
                </>
              )}

              {primaryAction && (
                <>
                  <div className="my-1 h-px bg-black/[0.08] mx-2" />
                  <div className="px-1 pt-1 pb-1">
                    <button
                      type="button"
                      onClick={() => {
                        primaryAction.onClick();
                        setOpen(false);
                      }}
                      className="flex items-center justify-center gap-2 w-full h-9 rounded-md text-[13px] font-semibold text-white bg-black/85 hover:bg-black transition-colors border-0 focus:outline-none"
                    >
                      {primaryAction.icon}
                      {primaryAction.label}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {mode === 'create' && (
            <div className="p-2">
              <div className="px-1 pb-2 text-[10px] font-semibold uppercase tracking-wider text-black/40">
                Name your folder
              </div>
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreate();
                  } else if (e.key === 'Escape') {
                    setMode('list');
                    setNewName('');
                  }
                }}
                placeholder="Folder name"
                className="w-full px-3 py-2 bg-white/70 border border-black/10 rounded-lg text-[13px] text-black/85 placeholder:text-black/35 outline-none focus:border-black/30 transition-colors"
              />
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setMode('list');
                    setNewName('');
                  }}
                  className="flex-1 h-8 rounded-md text-[12px] font-semibold text-black/70 hover:bg-black/[0.04] transition-colors bg-transparent border-0 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!newName.trim()}
                  className="flex-1 h-8 rounded-md text-[12px] font-semibold text-white bg-[var(--ink-5)] hover:bg-[var(--ink)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-0 focus:outline-none"
                >
                  Create
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
